/**
 * Auto captions. The browser speech API only hears a microphone, so this
 * records phrases while the timeline plays and the caller lays them on the
 * caption lane. A future file transcriber can replace `listenForCaptions`.
 */

interface SpeechResult {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechEvent {
  resultIndex: number;
  results: ArrayLike<SpeechResult>;
}

interface SpeechSession {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechEvent) => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechCtor = new () => SpeechSession;

function speechCtor(): SpeechCtor | null {
  if (typeof window === 'undefined') return null;
  const host = window as unknown as {
    SpeechRecognition?: SpeechCtor;
    webkitSpeechRecognition?: SpeechCtor;
  };
  return host.SpeechRecognition || host.webkitSpeechRecognition || null;
}

export function micCaptionSupported(): boolean {
  return speechCtor() !== null;
}

/** Start listening. `onFinal` receives each completed phrase. Returns a stop handle. */
export function listenForCaptions(onFinal: (text: string) => void): { stop: () => void } | null {
  const Ctor = speechCtor();
  if (!Ctor) return null;
  const session = new Ctor();
  session.continuous = true;
  session.interimResults = false;
  session.lang = 'en-US';
  session.onresult = (event) => {
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i];
      const text = result?.[0]?.transcript?.trim();
      if (result?.isFinal && text) onFinal(text);
    }
  };
  session.onerror = () => undefined;
  try {
    session.start();
  } catch {
    return null;
  }
  return {
    stop: () => {
      try {
        session.stop();
      } catch {
        /* already stopped */
      }
    },
  };
}
